import React, { useMemo, useState } from 'react';
import '../career/career.css';
import { Link } from 'react-router-dom';

const dataCareer = [
  { department: "All Careers", icon: <i className="bi bi-people-fill"></i> },
  { department: "Admission", icon: <i className="bi bi-pc-display"></i> },
  { department: "English teacher", icon: <i className="bi bi-journal-text"></i> },
  { department: "Consultant", icon: <i className="bi bi-person-workspace"></i> },
  { department: "Business Development", icon: <i className="bi bi-building"></i> },
  { department: "Admin Sales", icon: <i className="bi bi-bar-chart-line"></i> },
  { department: "Branch Manager", icon: <i className="bi bi-person-badge"></i> },
];

const CareerView = ({ data }) => {
  const [activeCategory, setActiveCategory] = useState("All Careers");
  const [searchQuery, setSearchQuery] = useState("");

  const toTitleCase = str =>
    str
      ? str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : '';

  // Group data to get counts
  const groupedData = useMemo(() => {
    const groups = data?.reduce((acc, item) => {
      const key = item.department;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {}) || {};
    return groups;
  }, [data]);

  // Filter jobs based on active category and search
  const filteredJobs = useMemo(() => {
    let filtered = data || [];

    // Category filter
    if (activeCategory !== "All Careers") {
      filtered = filtered.filter(item => item.department === activeCategory);
    }

    // Search filter
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(lowerQuery) ||
        item.department?.toLowerCase().includes(lowerQuery)
      );
    }

    return filtered;
  }, [data, activeCategory, searchQuery]);

  const formatRupiah = (value) => {
    if (value === null || value === undefined) return '-';

    const number = Number(value);

    if (isNaN(number)) return '-';

    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(number);
  }

  return (
    <div className="career-page-wrapper">
      {/* Hero Section */}
      <section className="career-hero">
        <div className="career-hero-overlay"></div>
        <div className="container">
          <div className="career-hero-content animate__animated animate__fadeInUp">
            <h1 className="career-hero-title">
              Find your <span className="red-box">dream Career</span> in<br />NIEC Indonesia
            </h1>
            <p className="career-hero-subtitle">
              When you're searching for a job, there are a few things you can do to get the most out of your search
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="career-container">
        <div className="career-bg-shape"></div>
        <div className="container">
          <div className="row g-5">
            {/* Left Sidebar */}
            <div className="col-lg-4">
              <h2 className="career-sidebar-title">Recommended<br />Careers</h2>
              <p className="career-sidebar-subtitle">Jelajahi karir dan temukan karir favoritmu.</p>

              <div className="career-categories-wrapper">
                <h3 className="career-categories-title">Careers Categories</h3>

                {/* Search Box */}
                <div className="career-search-box">
                  <div className="career-search-icon">
                    <i className="bi bi-search"></i>
                  </div>
                  <input
                    type="text"
                    className="career-search-input"
                    placeholder="Career title or keyword"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="career-search-location">
                    <i className="bi bi-geo-alt me-1"></i> Any Location
                  </div>
                </div>

                {/* Categories List */}
                <div className="category-list">
                  {/* All Careers Option */}
                  <div
                    className={`category-item ${activeCategory === "All Careers" ? "active" : ""}`}
                    onClick={() => setActiveCategory("All Careers")}
                  >
                    <div className="d-flex align-items-center">
                      <div className="category-item-icon">
                        <i className="bi bi-people-fill"></i>
                      </div>
                      <div className="category-item-name">All Careers</div>
                    </div>
                    <div className="category-item-count">{data?.length || 0} Orang</div>
                  </div>

                  {/* Dynamic Categories */}
                  {dataCareer.slice(1).map((cat, idx) => {
                    const count = groupedData[cat.department]?.length || 0;
                    if (count === 0 && activeCategory !== cat.department) return null; // hide empty categories unless active

                    return (
                      <div
                        key={idx}
                        className={`category-item ${activeCategory === cat.department ? "active" : ""}`}
                        onClick={() => setActiveCategory(cat.department)}
                      >
                        <div className="d-flex align-items-center">
                          <div className="category-item-icon">
                            {cat.icon}
                          </div>
                          <div className="category-item-name">{cat.department}</div>
                        </div>
                        <div className="category-item-count">{count} Orang</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Job List */}
            <div className="col-lg-8">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job, idx) => {
                  // Get icon for the job
                  const careerItem = dataCareer.find(c => c.department.toLowerCase() === job.department?.toLowerCase());
                  const icon = careerItem ? careerItem.icon : <i className="bi bi-briefcase"></i>;

                  return (
                    <div className="job-card animate__animated animate__fadeInUp" key={idx} style={{ animationDelay: `${idx * 0.1}s` }}>
                      {/* <div className="job-card-bg-shape"></div> */}
                      <div className="job-card-content">
                        <div className="job-card-header">
                          <div className="job-card-icon">
                            {icon}
                          </div>
                          <div>
                            <div className="job-card-meta">Department: <strong>{job.department}</strong> <span className="ms-3">Type: <strong>Full Time</strong></span></div>
                            <h3 className="job-card-title">{job.title}</h3>
                            <div className="job-card-salary">{formatRupiah(job.gaji_1)} - {formatRupiah(job.gaji_2)}</div>
                          </div>
                        </div>
                        <div className="job-card-footer">
                          <div className="job-card-location">
                            <i className="bi bi-geo-alt-fill"></i> {toTitleCase(job.propinsi?.nama_kabkota)}
                          </div>
                          <Link to={`${process.env.PUBLIC_URL}/career/${job.slug_title}`} className="job-card-btn">
                            Apply Now
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center p-5 bg-white rounded-3 shadow-sm">
                  <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
                  <h5>Belum ada lowongan</h5>
                  <p className="text-muted">Tidak ada lowongan yang sesuai dengan kriteria pencarian Anda.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerView;